import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { Subscription } from 'rxjs';
import { Metric, MetricsCardComponent, StateMessageConfig } from '@groundworks/ngx-groundworks-ui';
import { ConfigService } from '../../../config.service';
import { BranchAlertsMetric, BranchInfo } from '../../../sdk/sdk';
import { BranchMetricsService } from '../../../services/branch-metrics.service';
import { createAlertPopoverComponent } from './alert-popover.component';
import { AlertPopoverService } from './alert-popover.service';

export interface MetricsCard {
  metrics: Metric[];
  state: StateMessageConfig | undefined;
}

@Component({
  selector: 'app-weekly-calendar-metrics',
  imports: [MetricsCardComponent],
  templateUrl: './weekly-calendar-metrics.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeeklyCalendarMetricsComponent {
  private static readonly SLOT_PLANNED = 0;
  private static readonly SLOT_REVENUE = 1;
  private static readonly SLOT_CAPACITY = 2;
  private static readonly SLOT_ALERTS = 3;

  private static readonly INITIAL_STATE: StateMessageConfig = { type: 'initial', message: 'Select a branch to view metrics.' };
  private static readonly LOADING_STATE: StateMessageConfig = { type: 'loading', message: 'Loading metrics...' };
  private static readonly ERROR_STATE: StateMessageConfig = { type: 'error', message: 'Unable to load metrics.' };

  /** Stand-in value for a card whose metric is never fetched — reads better than "No data available". */
  private static readonly BLANK_VALUE = '--';

  private readonly branchMetricsService = inject(BranchMetricsService);
  private readonly alertPopoverService = inject(AlertPopoverService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private metricsSubscription?: Subscription;

  /**
   * MOD-4787: read once at construction. When off, the Weekly Plan / Projected Revenue /
   * Remaining Capacity cards still render — title only, no value, no state message — and
   * their endpoints are never called. Alerts are not gated: they keep fetching as before.
   */
  private readonly metricsEnabled = inject(ConfigService).enableCalendarMetrics;

  selectedBranch = input<BranchInfo | null>(null);
  startDate = input<Date | null>(null);
  paused = input(false);

  cards: MetricsCard[] = [];

  private lastLoadedKey: string | null = null;

  private readonly loadKey = computed(() => {
    const branch = this.selectedBranch();
    const start = this.startDate();
    if (branch?.id == null || start == null) return null;
    return `${branch.id}|${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
  });

  constructor() {
    effect(() => {
      const key = this.loadKey();
      if (key == null) {
        this.resetToInitial();
        return;
      }
      if (this.paused() || key === this.lastLoadedKey) return;
      this.lastLoadedKey = key;
      this.loadMetrics();
    });
    this.destroyRef.onDestroy(() => this.metricsSubscription?.unsubscribe());
  }

  private resetToInitial(): void {
    this.metricsSubscription?.unsubscribe();
    this.lastLoadedKey = null;
    this.cards = this.buildInitialCards();
    this.cdr.markForCheck();
  }

  private buildInitialCards(): MetricsCard[] {
    return this.buildCards(WeeklyCalendarMetricsComponent.INITIAL_STATE);
  }

  private buildLoadingCards(): MetricsCard[] {
    return this.buildCards(WeeklyCalendarMetricsComponent.LOADING_STATE);
  }

  /**
   * Four cards in the given state. With metrics gated off the three revenue/capacity slots show
   * their title over a `--` placeholder, so they never show a loading, initial or error message
   * for data that is never requested.
   */
  private buildCards(state: StateMessageConfig): MetricsCard[] {
    const cards: MetricsCard[] = Array.from({ length: 4 }, () => ({ metrics: [], state }));
    if (this.metricsEnabled) return cards;

    const startDate = this.startDate();
    const isPastWeek = startDate != null && this.isWeekInPast(startDate);
    cards[WeeklyCalendarMetricsComponent.SLOT_PLANNED] = this.blankCard('Weekly Plan');
    cards[WeeklyCalendarMetricsComponent.SLOT_REVENUE] = this.blankCard(isPastWeek ? 'Actual Revenue' : 'Projected Revenue');
    cards[WeeklyCalendarMetricsComponent.SLOT_CAPACITY] = this.blankCard(isPastWeek ? 'Missed Capacity' : 'Remaining Capacity');
    return cards;
  }

  private blankCard(title: string): MetricsCard {
    return { metrics: [{ title, highlightText: WeeklyCalendarMetricsComponent.BLANK_VALUE }], state: undefined };
  }

  private loadMetrics(): void {
    this.metricsSubscription?.unsubscribe();

    const branchId = this.selectedBranch()!.id!;
    const startDate = this.startDate()!;

    this.cards = this.buildLoadingCards();
    this.cdr.markForCheck();

    const subscription = new Subscription();

    if (this.metricsEnabled) {
      this.addRevenueAndCapacity(subscription, branchId, startDate);
    }

    subscription.add(
      this.branchMetricsService.getWeeklyAlerts(branchId, startDate).subscribe({
        next: (alerts) => {
          this.alertPopoverService.clear();
          this.cards[WeeklyCalendarMetricsComponent.SLOT_ALERTS] = {
            metrics: this.buildAlertMetrics(alerts),
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[WeeklyCalendarMetricsComponent.SLOT_ALERTS] = { metrics: [], state: WeeklyCalendarMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    this.metricsSubscription = subscription;
  }

  private addRevenueAndCapacity(subscription: Subscription, branchId: string, startDate: Date): void {
    const isPastWeek = this.isWeekInPast(startDate);

    subscription.add(
      this.branchMetricsService.getWeeklyPlannedRevenue(branchId, startDate).subscribe({
        next: (metric) => {
          this.cards[WeeklyCalendarMetricsComponent.SLOT_PLANNED] = {
            metrics: metric?.revenue != null
              ? [{ title: 'Weekly Plan', highlightText: this.formatCurrency(metric.revenue) }]
              : [{ title: 'Weekly Plan' }],
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[WeeklyCalendarMetricsComponent.SLOT_PLANNED] = { metrics: [], state: WeeklyCalendarMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    const revenueTitle = isPastWeek ? 'Actual Revenue' : 'Projected Revenue';

    subscription.add(
      this.branchMetricsService.getWeeklyRevenue(branchId, startDate).subscribe({
        next: (metric) => {
          this.cards[WeeklyCalendarMetricsComponent.SLOT_REVENUE] = {
            metrics: [{ title: revenueTitle, highlightText: this.formatCurrency(metric.revenue) }],
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[WeeklyCalendarMetricsComponent.SLOT_REVENUE] = { metrics: [], state: WeeklyCalendarMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );

    const capacityTitle = isPastWeek ? 'Missed Capacity' : 'Remaining Capacity';

    subscription.add(
      this.branchMetricsService.getWeeklyCapacity(branchId, startDate).subscribe({
        next: (capacity) => {
          this.cards[WeeklyCalendarMetricsComponent.SLOT_CAPACITY] = {
            metrics: [{ title: capacityTitle, highlightText: this.formatCurrency(capacity.capacity) }],
            state: undefined,
          };
          this.cdr.detectChanges();
        },
        error: () => {
          this.cards[WeeklyCalendarMetricsComponent.SLOT_CAPACITY] = { metrics: [], state: WeeklyCalendarMetricsComponent.ERROR_STATE };
          this.cdr.detectChanges();
        },
      }),
    );
  }

  private buildAlertMetrics(alerts: BranchAlertsMetric[]): Metric[] {
    if (alerts.length === 0) {
      return [{ title: 'Alerts', highlightText: '0' }];
    }

    const sorted = [...alerts].sort((a, b) => a.alertMetric.ordinal - b.alertMetric.ordinal);
    return sorted.map(a => {
      const type = a.alertMetric.type!;
      this.alertPopoverService.setProjectsForType(type, a.alertMetric.projects ?? []);

      const metric: Metric = {
        title: a.alertMetric.label!,
        highlightText: String(a.alertMetric.count).padStart(2, '0'),
      };
      if (a.alertMetric.count > 0) {
        metric.popoverTitle = a.alertMetric.userAction!;
        metric.popoverDetails = createAlertPopoverComponent(type);
      }
      return metric;
    });
  }

  private isWeekInPast(weekStartDate: Date): boolean {
    const sunday = new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), weekStartDate.getDate() + 6);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return sunday < today;
  }

  private formatCurrency(amount: number): string {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
}
